import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Activity, Clock, Flame, ArrowUpRight, ArrowDownRight, Layers, ChartLine, Table2, ArrowDownUp, Download } from 'lucide-react';
import baseapi from '../../baseapi';
import { useTheme } from '../../context/ThemeContext';

/**
 * Devotees for Darshan - peak trends, in vs out (Overview, 14 Oct 2026).
 *
 * In  = people entering the Antharalayam through the four darshan doors (100 / 300 / 500 Rs and the free line)
 * Out = people leaving through the two Antharalayam exits (192.168.2.44 and .125)
 */
const DOORS = ['CP-TEMPLE-ANTARALAYAM-ENTRY-3', 'CP-ANTHARALAYAM-ENTRY', 'CP-TEMPLE-ANTARALAYAM-ENTRY-2', 'CP-VIEW-2-190'];
const EXITS = ['CP-TEMPLE-ANTARALAYAM-EXIT', 'CP-VIEW-2-125'];
// The whole day so far in every option; the option is the bar size (17 Oct 2026: "5 min" was the last 5 minutes,
// a handful of bars - operators expect the day in 5-minute bars)
const WINDOWS = [
  { key: '5', label: '5 min', bucket: 5 },
  { key: '15', label: '15 min', bucket: 15 },
  { key: '30', label: '30 min', bucket: 30 },
  { key: '60', label: '1 hour', bucket: 60 },
  { key: '120', label: '2 hours', bucket: 120 },
] as const;
type WindowKey = typeof WINDOWS[number]['key'];
interface SeriesPoint { t: number; toward: number; away: number }
interface Minute { inn: number; out: number; any: number; outAny: number }
interface Official { inn: number | null; out: number | null; inside: number | null }
interface Placement { periods: { from: number; to: number; amount: number }[]; unplaced: number; fill: Record<string, number> }

const IST = 19800;
const hm = (t: number) => new Date(t * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata' });
const fmt = (v: number) => Math.round(v).toLocaleString('en-IN');
// people per minute: always whole people, never "2.1" or "0.5" (14 Oct 2026)
const rate = (v: number) => Math.round(v).toLocaleString('en-IN');
// whole people for the "crowd inside" sentence
const people = (v: number) => Math.max(1, Math.round(v)).toLocaleString('en-IN');
const dur = (m: number) => (m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}` : `${m} min`);
// temple palette (14 Oct 2026): In = saffron, Out = kumkum crimson, busiest hour = gold, darshan closed = steel blue
// #4D8DBA (the only cool colour, so "closed" never reads as data). In / Out checked to stay apart in both themes,
// also for colour-blind viewers; the dark theme uses brighter steps of the same hues.
const IN_COLOR = '#E6781B';
const IN_COLOR_DARK = '#F08A2C';
// const OUT_COLOR = '#BE123C';
// const OUT_COLOR_DARK = '#F43F5E';
const PEAK_HOUR = { light: '#E6781B', dark: '#F08A2C' };   // orange (matching first color) instead of brown
const CLOSED = { light: { hatch: '#4D8DBA', edge: '#4D8DBA', label: '#336A91' }, dark: { hatch: '#4D8DBA', edge: '#6FA6CC', label: '#A3C8E2' } };
const PAUSE_MAX = 5, PAUSE_MIN = 10, STARTED = 20;
interface Pause { start: number; end: number; minutes: number; ongoing: boolean; outMoving: boolean }

// Day line chart (17 Oct 2026, replaces the Today bars): devotees entering per bucket for each date of a range, from
// GET dashboard/darshan-entering/buckets, one line per date on a midnight-to-midnight axis.
const LINE_BUCKETS = [1, 5, 10, 15, 30, 60] as const;
// the latest date takes the saffron of the bars, earlier dates the next colours
const DAY_COLORS = {
  light: ['#E6781B', '#BE123C', '#0F766E', '#7C3AED', '#CA8A04', '#2563EB', '#65A30D', '#64748B'],
  dark: ['#F08A2C', '#F43F5E', '#2DD4BF', '#A78BFA', '#FACC15', '#60A5FA', '#A3E635', '#94A3B8'],
};
interface Bucket { start: string; end: string; t: number; minutes: number; count: number }
interface BucketDay { date: string; total: number; peak: { start: string; end: string; count: number } | null; buckets: Bucket[] }
const istDate = (ms = Date.now()) => new Date(ms + IST * 1000).toISOString().slice(0, 10);
const addDays = (d: string, n: number) => new Date(Date.parse(d) + n * 86_400_000).toISOString().slice(0, 10);
const minuteOf = (hhmm: string) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };
const clock = (m: number) => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const dayName = (d: string, today: string) =>
  d === today ? 'Today' : new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

async function series(cp: string): Promise<SeriesPoint[]> {
  const r = await baseapi.get(`checkpoints/${encodeURIComponent(cp)}/series?minutes=1440`);
  return (r.data?.points || []) as SeriesPoint[];
}
const busier = (points: SeriesPoint[]): 'toward' | 'away' =>
  points.reduce((a, p) => a + (p.away || 0), 0) > points.reduce((a, p) => a + (p.toward || 0), 0) ? 'away' : 'toward';

// Modern Glassmorphic Tooltip with clean typography
function CustomTooltip({ active, payload, bucket }: any) {
  if (!active || !payload || !payload.length) return null;
  const data = payload[0]?.payload;
  const inVal = data?.inn ?? 0;                // the real count of the piece, not the drawn (scaled) value
  // const outVal = payload.find((p: any) => p.dataKey === 'out')?.value ?? 0;
  const mins = data?.minutes || bucket || 1;

  return (
    <div className="bg-white/95 dark:bg-[#161822]/95 backdrop-blur-md p-2.5 sm:p-3 rounded-xl border border-neutral-200 dark:border-neutral-700/70 shadow-xl text-xs space-y-1.5 min-w-[190px]">
      <div className="font-bold text-neutral-800 dark:text-neutral-100 border-b border-neutral-100 dark:border-neutral-800 pb-1.5 flex items-center justify-between">
        <span>{mins > 1 ? `${hm(data.t)} – ${hm(data.t + mins * 60)}` : hm(data.t)}{data?.closed ? ' · Darshan closed' : ''}</span>
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400">
          {mins} min
        </span>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between gap-3 text-[#C2620F] dark:text-orange-400">
          <span className="flex items-center gap-1.5 font-medium">
            <span className="w-2 h-2 rounded-full bg-[#E6781B] dark:bg-[#F08A2C] shadow-xs" />
            Entered:
          </span>
          <span className="font-bold text-neutral-900 dark:text-white tabular-nums">
            {fmt(inVal)} {mins > 1 ? <span className="text-[10.5px] text-neutral-600 dark:text-neutral-300 font-semibold">({rate(inVal / mins)} per min)</span> : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

function DayTooltip({ active, payload, label, bucket, primary }: any) {
  if (!active || !payload?.length) return null;
  const from = label - bucket / 2;
  // newest date first, as the legend
  const rows = [...payload].sort((a: any, b: any) => String(b.dataKey).localeCompare(String(a.dataKey)));
  return (
    <div className="bg-white/95 dark:bg-[#161822]/95 backdrop-blur-md px-3 py-2.5 rounded-xl border border-neutral-200 dark:border-neutral-700/70 shadow-xl text-xs min-w-[200px]">
      <div className="flex items-center justify-between gap-3 pb-1.5 mb-1.5 border-b border-neutral-100 dark:border-neutral-800">
        <span className="font-black text-neutral-900 dark:text-neutral-50 tabular-nums">{bucket > 1 ? `${clock(from)} – ${clock(from + bucket)}` : clock(from)}</span>
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400">
          {bucket === 60 ? '1 hour' : `${bucket} min`}
        </span>
      </div>
      <div className="space-y-1">
        {rows.map((p: any) => (
          <div key={p.dataKey} className={`flex items-center justify-between gap-4 ${p.dataKey === primary ? '' : 'opacity-80'}`}>
            <span className="flex items-center gap-1.5 font-semibold text-neutral-700 dark:text-neutral-300">
              <span className="w-2 h-2 rounded-full" style={{ background: p.stroke || p.color }} />
              {p.name}
            </span>
            <span className="tabular-nums text-neutral-900 dark:text-white">
              <strong className="font-black">{fmt(p.value)}</strong>
              {bucket > 1 && <span className="ml-1 text-[10.5px] font-semibold text-neutral-500 dark:text-neutral-400">{rate(p.value / bucket)}/min</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const compact = (v: number) => (v >= 1000 ? `${+(v / 1000).toFixed(1)}k` : `${v}`);

function DayLines({ light }: { light: boolean }) {
  const today = istDate();
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [bucket, setBucket] = useState(5);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [focus, setFocus] = useState<string | null>(null);
  // the same counts as a line chart or as a table (17 Oct 2026); the choice is remembered in this browser
  const [mode, setMode] = useState<'chart' | 'table'>(() => {
    try { return localStorage.getItem('devotee_day_view') === 'table' ? 'table' : 'chart'; } catch { return 'chart'; }
  });
  useEffect(() => {
    try { localStorage.setItem('devotee_day_view', mode); } catch { /* not remembered */ }
  }, [mode]);
  const [newestFirst, setNewestFirst] = useState(true);
  // the answer is kept with the request it belongs to, so a new choice shows "Loading" until its own data is in
  const req = `${from}|${to}|${bucket}`;
  const [got, setGot] = useState<{ req: string; days: BucketDay[] | null; error: string | null; at?: number } | null>(null);
  const days = got?.req === req ? got.days : null;
  const error = got?.req === req ? got.error : null;
  const updated = got?.req === req ? got.at : undefined;

  useEffect(() => {
    let gone = false;
    const key = `${from}|${to}|${bucket}`;
    const get = () => baseapi.get('dashboard/darshan-entering/buckets', { params: { from, to, bucket } })
      .then(r => { if (!gone) setGot({ req: key, days: r.data?.days || [], error: null, at: Date.now() }); })
      .catch(e => { if (!gone) setGot(g => ({ req: key, days: g?.req === key ? g.days : null, at: g?.req === key ? g.at : undefined, error: e?.response?.data?.detail || 'Counts not available right now. Retrying…' })); });
    get();
    // auto refresh every minute, whatever the range (required: today keeps growing, and corrections can be added
    // to past days)
    const id = window.setInterval(get, 60_000);
    return () => { gone = true; window.clearInterval(id); };
  }, [from, to, bucket]);

  // the server takes at most 31 days: the other date follows along
  const pickFrom = (d: string) => {
    if (!d) return;
    setFrom(d);
    if (to < d) setTo(d); else if (addDays(d, 30) < to) setTo(addDays(d, 30));
  };
  const pickTo = (d: string) => {
    if (!d) return;
    setTo(d);
    if (from > d) setFrom(d); else if (addDays(d, -30) > from) setFrom(addDays(d, -30));
  };
  const toggle = (key: string) => setHidden(h => { const n = new Set(h); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  const palette = light ? DAY_COLORS.light : DAY_COLORS.dark;
  const lines = (days || []).map((d, i, all) => ({
    key: `d${d.date.replace(/-/g, '')}`,
    date: d.date,
    label: dayName(d.date, today),
    total: d.total,
    color: palette[(all.length - 1 - i) % palette.length],
    // today's current bucket is left out until complete: a part-filled point read as a sudden drop
    buckets: d.buckets.filter(b => b.minutes === bucket),
  }));
  const visible = lines.filter(l => !hidden.has(l.key));
  // the line in front: the one pointed at in the legend, else the latest date shown
  const primary = visible.find(l => l.key === focus) ?? visible[visible.length - 1];

  // each point sits in the middle of its bucket, so the lines run the full width of the day
  const rows = new Map<number, Record<string, number>>();
  visible.forEach(l => l.buckets.forEach(b => {
    const x = minuteOf(b.start) + bucket / 2;
    const r = rows.get(x) || { x };
    r[l.key] = b.count;
    rows.set(x, r);
  }));
  const data = [...rows.values()].sort((a, b) => a.x - b.x);
  const domain: [number, number] = [0, data.length ? data[data.length - 1].x + bucket / 2 : 1440];
  const ticks: number[] = [];
  {
    const span = domain[1] - domain[0];
    const step = span > 12 * 60 ? 120 : span > 3 * 60 ? 60 : 30;
    for (let m = domain[0]; m <= domain[1]; m += step) ticks.push(m);
  }
  const total = visible.reduce((a, l) => a + l.total, 0);
  const title = from === to ? (from === today ? 'Devotees Entering Today' : `Devotees Entering · ${dayName(from, today)}`)
    : `Devotees Entering · ${dayName(from, today)} – ${dayName(to, today)}`;
  const axis = { fontSize: 11, fontWeight: 600, fill: light ? '#6B625C' : '#8f97aa' };
  const ring = 'outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60';
  const dateInput = `w-[128px] px-2 py-1 text-xs font-bold rounded-lg bg-white dark:bg-[#252a3a] text-neutral-800 dark:text-neutral-100 shadow-sm dark:[color-scheme:dark] cursor-pointer ${ring}`;
  const cardBg = light ? '#ffffff' : '#181b23';

  // table (17 Oct 2026): plain, no colour per date; newest date first, as the legend. One date: entered, per minute
  // and the running total; several dates: one column of entered per date
  const tableCols = [...visible].reverse().map(l => {
    let sum = 0;
    const soFar = new Map<number, number>();
    l.buckets.forEach(b => { sum += b.count; soFar.set(minuteOf(b.start), sum); });
    return { ...l, soFar, peak: l.buckets.reduce<Bucket | null>((p, b) => (!p || b.count > p.count ? b : p), null) };
  });
  const single = tableCols.length === 1;
  const span = (start: number) => (bucket > 1 ? `${clock(start)} – ${clock(start + bucket)}` : clock(start));
  const isPeakAt = (l: typeof tableCols[number], start: number) => !!l.peak && l.peak.count > 0 && minuteOf(l.peak.start) === start;

  // Excel: a UTF-8 CSV that Excel opens as is (as the Darshan Completed export), earliest time first, plain numbers
  const exportExcel = () => {
    if (!tableCols.length || !data.length) return;
    const head = single ? ['Date', 'From', 'To', 'Devotees entered', ...(bucket > 1 ? ['Per minute'] : []), 'Total so far']
      : ['From', 'To', ...tableCols.map(l => l.date)];
    const body = data.map(r => {
      const start = r.x - bucket / 2;
      const time = [clock(start), clock(start + bucket)];
      if (!single) return [...time, ...tableCols.map(l => r[l.key] ?? '')];
      const l = tableCols[0], v = r[l.key];
      return [l.date, ...time, v ?? '', ...(bucket > 1 ? [v != null ? Math.round(v / bucket) : ''] : []), l.soFar.get(start) ?? ''];
    });
    const foot = single ? ['Total', '', '', tableCols[0].total, ...(bucket > 1 ? [''] : []), ''] : ['Total', '', ...tableCols.map(l => l.total)];
    const csv = '\uFEFF' + [head, ...body, foot].map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `devotees-entering-${from === to ? from : `${from}-to-${to}`}-${bucket}min.csv`;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const th = 'bg-neutral-50 dark:bg-[#1c2029] px-4 py-2.5 text-[11px] font-bold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-[#2a2f3c]';
  const td = 'px-4 py-2 border-b border-neutral-100 dark:border-[#222735]';
  const tf = 'bg-neutral-100 dark:bg-[#1f232d] px-4 py-2.5 font-black text-neutral-900 dark:text-neutral-100 border-t border-neutral-200 dark:border-[#2a2f3c]';
  const count = (v: number | undefined, peak: boolean) => (
    <span className="inline-flex items-center justify-end gap-1.5">
      {peak && <span className="px-1.5 py-px rounded text-[10px] font-black bg-orange-500/10 text-orange-700 dark:text-orange-300">Peak</span>}
      <span className={v == null ? 'text-neutral-400 dark:text-neutral-600'
        : v === 0 ? 'text-neutral-400 dark:text-neutral-500'
          : `text-neutral-900 dark:text-neutral-100 ${peak ? 'font-black' : 'font-semibold'}`}>
        {v == null ? '—' : fmt(v)}
      </span>
    </span>
  );
  const placeholder = (
    <div className={`h-full flex items-center justify-center rounded-lg text-xs text-neutral-600 dark:text-neutral-400 ${!days && !error ? 'animate-pulse bg-neutral-50 dark:bg-[#1c2029]' : ''}`}>
      {error ?? (days ? (lines.length ? 'All dates hidden – click a date above to show it' : 'No counts for these dates') : 'Loading data…')}
    </div>
  );

  return (
    <div className="p-3.5 sm:p-4 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/80 dark:border-[#262b37] shadow-xs transition-colors">
      <div className="flex flex-wrap items-center gap-3 justify-between pb-3 border-b border-neutral-100 dark:border-[#262c39]">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="p-1 rounded-lg bg-orange-500/10 text-orange-500 dark:bg-orange-500/15">
              <Activity className="w-4 h-4 stroke-[2.5]" />
            </span>
            <h3 className="text-sm sm:text-base font-black text-neutral-900 dark:text-neutral-100 tracking-tight">{title}</h3>
          </div>
          <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5">
            {days ? <><strong className="text-neutral-900 dark:text-neutral-100 font-black">{fmt(total)}</strong> devotees entered · </> : ''}
            each point = {bucket === 60 ? '1 hour' : `${bucket} minutes`}
            {updated ? ` · updated ${hm(updated / 1000)}` : ''}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 p-1 rounded-xl bg-neutral-100 dark:bg-[#13151c] border border-neutral-200/80 dark:border-[#262c39] shadow-inner">
            {(['chart', 'table'] as const).map(v => (
              <button
                key={v}
                onClick={() => setMode(v)}
                aria-pressed={mode === v}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold rounded-lg border transition-all duration-150 cursor-pointer ${ring} ${mode === v
                  ? 'bg-white dark:bg-[#252a3a] text-orange-600 dark:text-orange-400 shadow-sm border-neutral-200/60 dark:border-neutral-700/80'
                  : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200 hover:bg-white/50 dark:hover:bg-neutral-800/40'
                  }`}
              >
                {v === 'chart' ? <ChartLine className="w-3.5 h-3.5 stroke-[2.5]" /> : <Table2 className="w-3.5 h-3.5 stroke-[2.5]" />}
                {v === 'chart' ? 'Chart' : 'Table'}
              </button>
            ))}
          </div>
          <button
            onClick={exportExcel}
            disabled={!days || !data.length}
            title="Download these counts as a file that opens in Excel"
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl border border-neutral-200 dark:border-[#2a2f3c] bg-white dark:bg-[#1c2029] text-neutral-700 dark:text-neutral-200 shadow-sm hover:bg-neutral-50 dark:hover:bg-[#232838] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${ring}`}
          >
            <Download className="w-3.5 h-3.5 stroke-[2.5]" />
            Excel
          </button>
          <div className="flex items-center gap-1.5 p-1 rounded-xl bg-neutral-100 dark:bg-[#13151c] border border-neutral-200/80 dark:border-[#262c39] shadow-inner text-xs font-bold text-neutral-500 dark:text-neutral-400">
            <span className="pl-1.5">From</span>
            <input type="date" aria-label="From date" value={from} max={today} onChange={e => pickFrom(e.target.value)} className={dateInput} />
            <span>to</span>
            <input type="date" aria-label="To date" value={to} max={today} onChange={e => pickTo(e.target.value)} className={dateInput} />
          </div>
          <div className="flex items-center gap-1 p-1 rounded-xl bg-neutral-100 dark:bg-[#13151c] border border-neutral-200/80 dark:border-[#262c39] shadow-inner">
            {LINE_BUCKETS.map(b => (
              <button
                key={b}
                onClick={() => setBucket(b)}
                className={`px-2.5 py-1 text-xs font-bold rounded-lg border transition-all duration-150 cursor-pointer ${ring} ${bucket === b
                  ? 'bg-white dark:bg-[#252a3a] text-orange-600 dark:text-orange-400 shadow-sm border-neutral-200/60 dark:border-neutral-700/80'
                  : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200 hover:bg-white/50 dark:hover:bg-neutral-800/40'
                  }`}
              >
                {b === 60 ? '1h' : `${b}m`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* legend: point at a date to bring its line forward, click to hide or show it */}
      {lines.length > 1 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {[...lines].reverse().map(l => {
            const off = hidden.has(l.key);
            return (
              <button
                key={l.key}
                onClick={() => toggle(l.key)}
                onMouseEnter={() => setFocus(l.key)}
                onMouseLeave={() => setFocus(null)}
                title={off ? 'Show this date' : 'Hide this date'}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs transition-all cursor-pointer ${ring} ${off
                  ? 'border-dashed border-neutral-300 dark:border-neutral-700 opacity-50'
                  : l.key === primary?.key
                    ? 'border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-[#20242f]'
                    : 'border-neutral-200 dark:border-[#2a2f3c] hover:bg-neutral-50 dark:hover:bg-[#20242f]'}`}
              >
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: off ? 'transparent' : l.color, boxShadow: `inset 0 0 0 2px ${l.color}` }} />
                <span className={`font-bold text-neutral-800 dark:text-neutral-100 ${off ? 'line-through' : ''}`}>{l.label}</span>
                <span className="font-semibold text-neutral-500 dark:text-neutral-400 tabular-nums">{fmt(l.total)}</span>
              </button>
            );
          })}
        </div>
      )}

      {mode === 'chart' ? (
      <div className="mt-3 w-full h-[260px] sm:h-[300px] [&_*:focus]:outline-none">
        {days && data.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data} margin={{ top: 12, right: 12, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="devotee-day-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={primary?.color} stopOpacity={light ? 0.28 : 0.35} />
                  <stop offset="100%" stopColor={primary?.color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="4 4" vertical={false} stroke={light ? '#eef0f3' : '#232836'} />
              <XAxis
                dataKey="x"
                type="number"
                domain={domain}
                ticks={ticks}
                tickFormatter={(m: number) => clock(m)}
                tick={axis}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                allowDataOverflow
              />
              <YAxis tick={axis} tickLine={false} axisLine={false} allowDecimals={false} width={44} tickCount={5} tickFormatter={compact} />
              <Tooltip
                cursor={{ stroke: light ? '#cbd5e1' : '#475569', strokeWidth: 1 }}
                content={<DayTooltip bucket={bucket} primary={primary?.key} />}
              />
              {visible.filter(l => l !== primary).map(l => (
                <Line key={l.key} type="monotone" dataKey={l.key} name={l.label} stroke={l.color} strokeWidth={1.75}
                  strokeOpacity={focus ? 0.25 : 0.7} dot={false} isAnimationActive={false}
                  activeDot={{ r: 3.5, fill: l.color, stroke: cardBg, strokeWidth: 2 }} />
              ))}
              {primary && (
                <Area key={primary.key} type="monotone" dataKey={primary.key} name={primary.label} stroke={primary.color}
                  strokeWidth={2.75} fill="url(#devotee-day-fill)" dot={false} isAnimationActive={false}
                  activeDot={{ r: 5, fill: primary.color, stroke: cardBg, strokeWidth: 2.5 }} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        ) : placeholder}
      </div>
      ) : days && data.length ? (
        <div className="mt-3 max-h-[440px] overflow-auto rounded-lg border border-neutral-200 dark:border-[#262c39]">
          <table className="w-full text-xs tabular-nums border-separate border-spacing-0">
            <thead className="sticky top-0 z-20">
              <tr>
                <th className={`${th} sticky left-0 z-30 text-left w-[170px]`}>
                  <button onClick={() => setNewestFirst(v => !v)} title="Change the order"
                    className={`flex items-center gap-1.5 uppercase tracking-wide cursor-pointer hover:text-neutral-800 dark:hover:text-neutral-200 ${ring}`}>
                    Time <ArrowDownUp className="w-3 h-3" />
                  </button>
                </th>
                {single ? (
                  <>
                    <th className={`${th} text-right`}>Devotees entered</th>
                    {bucket > 1 && <th className={`${th} text-right`}>Per minute</th>}
                    <th className={`${th} text-right`}>Total so far</th>
                  </>
                ) : tableCols.map(l => (
                  <th key={l.key} className={`${th} text-right min-w-[120px]`}>
                    <div className="normal-case tracking-normal text-xs font-black text-neutral-800 dark:text-neutral-100">{l.label}</div>
                    <div className="normal-case tracking-normal font-semibold">{l.peak && l.peak.count > 0 ? `peak ${l.peak.start}` : '—'}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(newestFirst ? [...data].reverse() : data).map((r, i) => {
                const start = r.x - bucket / 2;
                // opaque row colours: the time column stays in place over the others when scrolled sideways
                const row = `${i % 2 ? 'bg-[#fafafa] dark:bg-[#1b1f28]' : 'bg-white dark:bg-[#181b23]'} group-hover:bg-orange-50 dark:group-hover:bg-[#262b38]`;
                const l0 = tableCols[0], v0 = r[l0.key] as number | undefined;
                return (
                  <tr key={r.x} className="group">
                    <td className={`${td} ${row} sticky left-0 z-10 font-semibold text-neutral-700 dark:text-neutral-300 whitespace-nowrap`}>{span(start)}</td>
                    {single ? (
                      <>
                        <td className={`${td} ${row} text-right`}>{count(v0, isPeakAt(l0, start))}</td>
                        {bucket > 1 && <td className={`${td} ${row} text-right text-neutral-600 dark:text-neutral-400`}>{v0 != null ? rate(v0 / bucket) : '—'}</td>}
                        <td className={`${td} ${row} text-right text-neutral-600 dark:text-neutral-400`}>{l0.soFar.has(start) ? fmt(l0.soFar.get(start)!) : '—'}</td>
                      </>
                    ) : tableCols.map(l => (
                      <td key={l.key} className={`${td} ${row} text-right`}>{count(r[l.key] as number | undefined, isPeakAt(l, start))}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="sticky bottom-0 z-20">
              <tr>
                <td className={`${tf} sticky left-0 z-30`}>Total</td>
                {single ? (
                  <>
                    <td className={`${tf} text-right`}>{fmt(tableCols[0].total)}</td>
                    {bucket > 1 && <td className={tf} />}
                    <td className={tf} />
                  </>
                ) : tableCols.map(l => (
                  <td key={l.key} className={`${tf} text-right`}>{fmt(l.total)}</td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="mt-3 h-[260px]">{placeholder}</div>
      )}
    </div>
  );
}

export interface DevoteeTrendMetrics {
  peak: { label: string; t: number; inn: number; minutes: number } | null;
  nowIn: number;
  nowOut: number;
  peakHour: [number, number] | null;
  bucket: number;
  winLabel?: string;
}

// The bar chart card can be hidden here; its data still loads, as the Overview's peak-hour card reads it
const SHOW_BARS = true;

export function DevoteeTrend({
  hideCards = true,
  onMetricsChange,
}: {
  hideCards?: boolean;
  onMetricsChange?: (m: DevoteeTrendMetrics) => void;
} = {}) {
  const { theme } = useTheme();
  const light = theme === 'light';
  const [win, setWin] = useState<WindowKey>(() => {
    // a new key (17 Oct 2026: the options became bar sizes), so everyone starts on 5 min once
    try { const k = localStorage.getItem('devotee_trend_bar_size'); return WINDOWS.some(x => x.key === k) ? k as WindowKey : '5'; } catch { return '5'; }
  });
  const [perMinute, setPerMinute] = useState<Map<number, Minute> | null>(null);
  const [official, setOfficial] = useState<Official>({ inn: null, out: null, inside: null });
  const [placement, setPlacement] = useState<Placement>({ periods: [], unplaced: 0, fill: {} });
  const [updated, setUpdated] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const barsRef = useRef<HTMLDivElement>(null);
  const [barsWidth, setBarsWidth] = useState(0);
  useEffect(() => {
    const el = barsRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setBarsWidth(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const load = useCallback(async () => {
    try {
      const [doors, exits, ov, adj] = await Promise.all([
        Promise.all(DOORS.map(series)),
        Promise.all(EXITS.map(series)),
        baseapi.get('overview').then(r => r.data?.kpis || {}).catch(() => ({})),
        baseapi.get('dashboard/adjust/periods').then(r => r.data || {}).catch(() => ({})),
      ]);
      setPlacement({ periods: adj.periods || [], unplaced: adj.unplaced || 0, fill: adj.fill_minutes || {} });
      setOfficial({ inn: ov.total ?? null, out: ov.darshan ?? ov.exited ?? null, inside: ov.antharalayam ?? null });
      const m = new Map<number, Minute>();
      const at = (t: number) => { let x = m.get(t); if (!x) { x = { inn: 0, out: 0, any: 0, outAny: 0 }; m.set(t, x); } return x; };
      doors.forEach(points => { const d = busier(points); points.forEach(p => { const x = at(p.t); x.inn += p[d] || 0; x.any += (p.toward || 0) + (p.away || 0); }); });
      exits.forEach(points => {
        const d = busier(points), o = d === 'toward' ? 'away' : 'toward';
        points.forEach(p => { const x = at(p.t); x.out += (p[d] || 0) - (p[o] || 0); x.outAny += (p.toward || 0) + (p.away || 0); });
      });
      setPerMinute(m);
      setUpdated(Date.now());
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 60_000);
    return () => window.clearInterval(id);
  }, [load]);

  useEffect(() => {
    try { localStorage.setItem('devotee_trend_bar_size', win); } catch { /* not remembered */ }
  }, [win]);

  const w = WINDOWS.find(x => x.key === win) || WINDOWS[0];

  const view = useMemo(() => {
    if (!perMinute || perMinute.size === 0) return null;
    const keys = [...perMinute.keys()];
    const end = Math.max(Math.max(...keys), Math.floor(Date.now() / 60_000) * 60 - 60);
    const dayStart = Math.min(...keys);

    const outFilled = new Map<number, number>();
    {
      let runStart: number | null = null, runIn = 0;
      const flush = (until: number) => {
        if (runStart !== null && (until - runStart) / 60 >= 10 && runIn >= 50)
          for (let mt = runStart; mt < until; mt += 60) outFilled.set(mt, perMinute.get(mt)?.inn || 0);
        runStart = null; runIn = 0;
      };
      for (let mt = dayStart; mt <= end; mt += 60) {
        const x = perMinute.get(mt);
        if (!(x?.outAny)) { if (runStart === null) runStart = mt; runIn += x?.inn || 0; } else flush(mt);
      }
      flush(end + 60);
    }

    const rawOut = (mt: number) => outFilled.has(mt) ? outFilled.get(mt)! : Math.max(0, perMinute.get(mt)?.out || 0);
    const counted = (mt: number) => { const x = perMinute.get(mt); return !!(x && (x.any || x.outAny)); };
    const weight = new Map<number, number>();
    for (let mt = dayStart; mt <= end; mt += 60) {
      if (counted(mt)) { weight.set(mt, perMinute.get(mt)?.inn || 0); continue; }
      let sum = 0, n = 0;
      for (let d = 60; d <= 30 * 60; d += 60) for (const nb of [mt - d, mt + d]) if (nb >= dayStart && nb <= end && counted(nb)) { sum += perMinute.get(nb)?.inn || 0; n++; }
      weight.set(mt, n ? sum / n : 0);
    }

    let opened = Infinity;
    for (let mt = dayStart; mt <= end; mt += 60) if ((perMinute.get(mt)?.inn || 0) >= STARTED) { opened = mt; break; }
    while (isFinite(opened) && opened - 60 >= dayStart && (perMinute.get(opened - 60)?.inn || 0) > PAUSE_MAX) opened -= 60;
    const morningClosed = isFinite(opened) && (opened - dayStart) / 60 >= PAUSE_MIN;
    if (morningClosed) for (let mt = dayStart; mt < opened; mt += 60) weight.set(mt, 0);

    const extra = new Map<number, number>();
    const place = (from: number, to: number, amount: number) => {
      const mins: number[] = [];
      for (let mt = Math.max(dayStart, Math.floor(from / 60) * 60); mt < Math.min(to, end + 60); mt += 60) mins.push(mt);
      if (!mins.length || !amount) return;
      const tw = mins.reduce((a2, mt) => a2 + (weight.get(mt) || 0), 0);
      mins.forEach(mt => extra.set(mt, (extra.get(mt) || 0) + amount * (tw ? (weight.get(mt) || 0) / tw : 1 / mins.length)));
    };

    placement.periods.forEach(pz => place(pz.from, pz.to, pz.amount));
    if (placement.unplaced) place(dayStart, end + 60, placement.unplaced);
    Object.entries(placement.fill).forEach(([t, v]) => { const mt = Number(t); extra.set(mt, (extra.get(mt) || 0) + v); });

    const IN = (mt: number) => Math.max(0, (perMinute.get(mt)?.inn || 0) + (extra.get(mt) || 0));

    let outDay = 0, completedTarget = official.out;
    for (let mt = dayStart; mt <= end; mt += 60) outDay += rawOut(mt) + (extra.get(mt) || 0);
    const outScale = completedTarget && outDay > 0 ? completedTarget / outDay : 1;
    const OUT = (mt: number) => Math.max(0, (rawOut(mt) + (extra.get(mt) || 0)) * outScale);

    const first = dayStart;
    const step = w.bucket * 60;
    const start = Math.floor((first + IST) / step) * step - IST;
    const bars: { t: number; x: number; label: string; inn: number; out: number; minutes: number }[] = [];

    for (let t = start; t <= end; t += step) {
      let inn = 0, out = 0, mins = 0;
      for (let mt = t; mt < t + step && mt <= end; mt += 60) {
        if (mt < first) continue;
        inn += IN(mt); out += OUT(mt); mins++;
      }
      if (mins) bars.push({ t, x: t + mins * 30, label: hm(t), inn: Math.round(inn), out: Math.round(out), minutes: mins });
    }

    const minutes = bars.reduce((a, b) => a + b.minutes, 0) || 1;
    let totIn = 0, totOut = 0;
    let camIn = 0, camOut = 0;
    for (let mt = first; mt <= end; mt += 60) { totIn += IN(mt); totOut += OUT(mt); camIn += perMinute.get(mt)?.inn || 0; camOut += Math.max(0, perMinute.get(mt)?.out || 0); }
    const peak = bars.reduce((p, b) => (b.inn / b.minutes > p.inn / p.minutes ? b : p), bars[0]);
    let nowIn = 0, nowOut = 0;
    for (let mt = end - 4 * 60; mt <= end; mt += 60) { nowIn += IN(mt); nowOut += OUT(mt); }

    // peak hour = the busiest 60 minutes in a row, not a clock hour (15 Oct 2026: the busiest clock hour 04:00-05:00
    // did not contain the busiest 15 minutes 05:30-05:45, because a darshan stop pulled 05:00-06:00 down - it read as
    // a contradiction). A sliding window always contains the peak 15 minutes.
    let peakHour: [number, number] | null = null;
    {
      let sum = 0;
      for (let mt = dayStart; mt <= end; mt += 60) {
        sum += IN(mt);
        if (mt - 3600 >= dayStart) sum -= IN(mt - 3600);
        const start = Math.max(dayStart, mt - 3540);
        if (mt - dayStart >= 3540 && (!peakHour || sum > peakHour[1])) peakHour = [start, Math.round(sum)];
      }
    }

    const pauses: Pause[] = morningClosed
      ? [{ start: dayStart, end: opened - 60, minutes: (opened - dayStart) / 60, ongoing: false, outMoving: false }] : [];
    const firstFlow = keys.filter(t => (perMinute.get(t)?.inn || 0) >= STARTED).reduce((a, t) => Math.min(a, t), Infinity);
    let run: { s: number; e: number; out: number; any: number } | null = null;
    const close = (ongoing: boolean) => {
      if (run && (run.e - run.s) / 60 + 1 >= PAUSE_MIN && run.out > 0 && run.any > 0)
        pauses.push({ start: run.s, end: run.e, minutes: (run.e - run.s) / 60 + 1, ongoing, outMoving: run.out > 0 });
      run = null;
    };
    for (let mt = isFinite(firstFlow) ? firstFlow : end + 60; mt <= end; mt += 60) {
      const x = perMinute.get(mt);
      // what the bars show (camera count + corrections placed in time): a gap filled in for cameras that were down
      // (16 Oct 2026: power cut 05:55-06:45, 3,400 placed) is not a darshan closure
      if (IN(mt) <= PAUSE_MAX) {
        if (!run) run = { s: mt, e: mt, out: 0, any: 0 };
        run.e = mt; run.out += Math.abs(x?.out || 0); run.any += x?.any || 0;
      } else close(false);
    }
    close(true);

    // The time axis is real clock time (14 Oct 2026). It used to be one slot per bucket, which snapped a closure to its
    // 15-minute slots: 10:18-10:30 fell inside the single 10:15 slot and was drawn zero pixels wide.
    const domain: [number, number] = [bars[0]?.t ?? end, end + 60];
    const span = (domain[1] - domain[0]) / 60;
    const tickStep = (span <= 45 ? 5 : span <= 75 ? 10 : span <= 150 ? 15 : span <= 360 ? 30 : 60) * 60;
    const ticks: number[] = [];
    for (let t = Math.ceil((domain[0] + IST) / tickStep) * tickStep - IST; t <= domain[1]; t += tickStep) ticks.push(t);
    if (!ticks.length || domain[1] - ticks[ticks.length - 1] > tickStep / 2) ticks.push(domain[1]);
    const shaded = pauses
      .map(pz => ({ ...pz, x1: Math.max(pz.start, domain[0]), x2: Math.min(pz.end + 60, domain[1]) }))
      .filter(pz => pz.x2 > pz.x1);

    const cstep = w.bucket * 60;
    const chart: { t: number; x: number; label: string; inn: number; out: number; minutes: number; plot: number; closed: boolean }[] = [];
    for (let t = Math.floor((domain[0] + IST) / cstep) * cstep - IST; t <= end; t += cstep) {
      let inn = 0, out = 0, mins = 0;
      for (let mt = t; mt < t + cstep && mt <= end; mt += 60) { if (mt < first) continue; inn += IN(mt); out += OUT(mt); mins++; }
      if (!mins) continue;
      const closed = shaded.some(pz => t >= pz.x1 && Math.min(t + cstep, end + 60) <= pz.x2);
      chart.push({ t, x: t + cstep / 2, label: hm(t), inn: Math.round(inn), out: Math.round(out), minutes: mins, plot: Math.round(inn), closed });
    }
    if (chart.length) domain[1] = Math.max(domain[1], chart[chart.length - 1].t + cstep);

    return {
      pauses, shaded, domain, ticks, chart, cstep,
      camIn, camOut, filledMinutes: outFilled.size,
      bars, totIn, totOut: Math.max(0, totOut), avgIn: totIn / minutes, avgOut: Math.max(0, totOut) / minutes,
      peak, nowIn: nowIn / 5, nowOut: Math.max(0, nowOut) / 5, peakHour: peakHour as [number, number] | null,
    };
  }, [perMinute, w, official, placement]);

  useEffect(() => {
    if (onMetricsChange && view) {
      onMetricsChange({
        peak: view.peak ? { label: view.peak.label, t: view.peak.t, inn: view.peak.inn, minutes: view.peak.minutes } : null,
        nowIn: view.nowIn,
        nowOut: view.nowOut,
        peakHour: view.peakHour,
        bucket: w.bucket,
        winLabel: WINDOWS.find(x => x.key === win)?.label,
      });
    }
  }, [view, w.bucket, win, onMetricsChange]);

  const cmin = (view?.cstep ?? w.bucket * 60) / 60;      // the chart's bar length
  const unit = cmin === 1 ? '1 minute' : `${cmin} minutes`;
  const axis = { fontSize: 11, fontWeight: 600, fill: light ? '#5F534B' : '#a3abbd' };
  const closed = light ? CLOSED.light : CLOSED.dark;
  const inColor = light ? IN_COLOR : IN_COLOR_DARK;

  // "Darshan Closed" boxes in a strip above the plot, centred over their band (left of the band's end while still
  // going on). Full day can have several closures close together (01:11, 01:26, 02:33 ...): a box that would overlap
  // the one before goes up a row
  const BOX_W = 124, BOX_H = 40, BOX_GAP = 6, PLOT_LEFT = 52, PLOT_RIGHT = 16;
  const boxes = new Map<number, { x: number; row: number }>();
  if (view) {
    const span = Math.max(1, view.domain[1] - view.domain[0]);
    const right = Math.max(PLOT_LEFT + BOX_W, barsWidth - PLOT_RIGHT);
    const px = (t: number) => PLOT_LEFT + ((t - view.domain[0]) / span) * (right - PLOT_LEFT);
    const rowEnds: number[] = [];
    [...view.shaded].sort((a, b) => a.x1 - b.x1).forEach(pz => {
      const want = pz.ongoing ? px(pz.x2) - BOX_W : (px(pz.x1) + px(pz.x2)) / 2 - BOX_W / 2;
      const x = Math.min(right - BOX_W, Math.max(PLOT_LEFT, want));
      let row = rowEnds.findIndex(e => e + BOX_GAP <= x);
      if (row < 0) { row = rowEnds.length; rowEnds.push(0); }
      rowEnds[row] = x + BOX_W;
      boxes.set(pz.start, { x, row });
    });
  }
  const boxRows = boxes.size ? Math.max(...[...boxes.values()].map(b => b.row)) + 1 : 0;
  const strip = boxRows * (BOX_H + BOX_GAP);

  return (
    <>
    {SHOW_BARS && (
    <div className="p-3.5 sm:p-4 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/80 dark:border-[#262b37] shadow-xs transition-colors">
      {/* Top Header & Range Filters */}
      <div className="flex flex-wrap items-center gap-3 justify-between pb-3 border-b border-neutral-100 dark:border-[#262c39]">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="p-1 rounded-lg bg-orange-500/10 text-orange-500 dark:bg-orange-500/15">
              <Layers className="w-4 h-4 stroke-[2.5]" />
            </span>
            <h3 className="text-sm sm:text-base font-black text-neutral-900 dark:text-neutral-100 tracking-tight">
              Devotees Entering for Darshan
            </h3>
          </div>
          <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5">
            People entering and leaving the Antharalayam · today so far · each bar = {unit}
            {updated ? ` · updated ${hm(updated / 1000)}` : ''}
          </p>
        </div>

        {/* Premium Segmented Tab Switcher */}
        <div className="flex items-center gap-1 p-1 rounded-xl bg-neutral-100 dark:bg-[#13151c] border border-neutral-200/80 dark:border-[#262c39] shadow-inner">
          {WINDOWS.map(x => {
            const isSelected = win === x.key;
            return (
              <button
                key={x.key}
                onClick={() => setWin(x.key)}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all duration-150 cursor-pointer ${isSelected
                  ? 'bg-white dark:bg-[#252a3a] text-orange-600 dark:text-orange-400 shadow-sm border border-neutral-200/60 dark:border-neutral-700/80 scale-[1.02]'
                  : 'text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200 hover:bg-white/50 dark:hover:bg-neutral-800/40'
                  }`}
              >
                {x.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 3 Redesigned KPI Cards matching the Project Design Language */}
      {!hideCards && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
          {/* Card 1: Peak Darshan Entry */}
          <div className="p-3 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/90 dark:border-[#262c39] shadow-xs hover:border-orange-500/40 transition-colors flex flex-col justify-between">
            <div className="flex items-start justify-between gap-2">
              <span className="flex items-center gap-1.5 text-sm font-bold text-orange-600 dark:text-orange-400">
                <Flame className="w-4 h-4 shrink-0 stroke-[2.5]" />
                <span>Peak {w.label} – most devotees entered</span>
              </span>
              <span className="text-[10px] sm:text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-600 dark:text-orange-400 shrink-0">
                Today
              </span>
            </div>

            <div className="mt-1 text-3xl sm:text-[42px] font-black text-neutral-900 dark:text-neutral-50 tracking-tight leading-tight">
              {view?.peak ? `${view.peak.label}–${hm(view.peak.t + w.bucket * 60)}` : '—'}
            </div>

            <div className="mt-1.5 pt-1 border-t border-neutral-100 dark:border-[#222735] flex items-center justify-between text-xs text-neutral-700 dark:text-neutral-300">
              <span>{view?.peak
                ? <><strong className="text-neutral-900 dark:text-neutral-100 font-black text-sm">{fmt(view.peak.inn)}</strong> devotees entered · about <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{rate(view.peak.inn / view.peak.minutes)}</strong> every minute</>
                : '—'}</span>
            </div>
          </div>

          {/* Card 2: Right Now */}
          <div className="p-3 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/90 dark:border-[#262c39] shadow-xs hover:border-[#BE123C]/40 transition-colors flex flex-col justify-between">
            <div className="flex items-start justify-between gap-2">
              <span className="flex items-center gap-1.5 text-sm font-bold text-emerald-600 dark:text-emerald-400">
                <Activity className="w-4 h-4 shrink-0 stroke-[2.5]" />
                <span>In &amp; out of Antharalayam</span>
              </span>
              <span className="text-[10px] sm:text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 shrink-0">
                Live
              </span>
            </div>

            <div className="mt-1 flex items-baseline gap-2.5">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl sm:text-[42px] font-black text-[#E6781B] dark:text-[#F08A2C] tracking-tight leading-tight">
                  {view ? fmt(view.nowIn * 5) : '—'}
                </span>
                <span className="text-sm sm:text-base font-bold text-[#C2620F] dark:text-orange-400">went in</span>
              </div>
              <span className="text-neutral-300 dark:text-neutral-700 text-lg">·</span>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl sm:text-[42px] font-black text-[#BE123C] dark:text-[#F43F5E] tracking-tight leading-tight">
                  {view ? fmt(view.nowOut * 5) : '—'}
                </span>
                <span className="text-sm sm:text-base font-bold text-[#BE123C]/80 dark:text-[#F43F5E]">came out</span>
              </div>
            </div>

            <div className="mt-1.5 pt-1 border-t border-neutral-100 dark:border-[#222735] flex items-center justify-between text-xs text-neutral-700 dark:text-neutral-300">
              {view ? (
                Math.abs(view.nowIn - view.nowOut) * 5 < 5 ? (
                  <span className="text-neutral-700 dark:text-neutral-200 font-bold">Crowd inside is steady – about as many came out as went in</span>
                ) : view.nowIn > view.nowOut ? (
                  <span className="text-[#C2620F] dark:text-amber-400 font-bold flex items-center gap-0.5">
                    <ArrowUpRight className="w-3.5 h-3.5 stroke-[2.5]" /> Crowd inside is growing – {people((view.nowIn - view.nowOut) * 5)} more people inside than 5 min ago
                  </span>
                ) : (
                  <span className="text-[#BE123C] dark:text-[#F43F5E] font-bold flex items-center gap-0.5">
                    <ArrowDownRight className="w-3.5 h-3.5 stroke-[2.5]" /> Crowd inside is going down – {people((view.nowOut - view.nowIn) * 5)} fewer people inside than 5 min ago
                  </span>
                )
              ) : (
                <span className="text-neutral-600 dark:text-neutral-400">Calculating…</span>
              )}
            </div>
          </div>

          {/* Card 3: Busiest Hour */}
          <div className="p-3 rounded-xl bg-white dark:bg-[#181b23] border border-neutral-200/90 dark:border-[#262c39] shadow-xs hover:border-orange-500/40 transition-colors flex flex-col justify-between">
            <div className="flex items-start justify-between gap-2">
              <span className="flex items-center gap-1.5 text-sm font-bold text-orange-600 dark:text-orange-400">
                <Clock className="w-4 h-4 shrink-0 stroke-[2.5]" />
                <span>Peak hour – most devotees entered</span>
              </span>
              <span className="text-[10px] sm:text-[10.5px] font-bold px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-600 dark:text-orange-400 shrink-0">
                Today
              </span>
            </div>

            <div className="mt-1 text-3xl sm:text-[42px] font-black text-neutral-900 dark:text-neutral-50 tracking-tight leading-tight">
              {view?.peakHour ? `${hm(view.peakHour[0])}–${hm(view.peakHour[0] + 3600)}` : '—'}
            </div>

            <div className="mt-1.5 pt-1 border-t border-neutral-100 dark:border-[#222735] flex items-center justify-between text-xs text-neutral-700 dark:text-neutral-300">
              <span>{view?.peakHour ? <><strong className="text-neutral-900 dark:text-neutral-100 font-black text-sm">{fmt(view.peakHour[1])}</strong> devotees entered · about <strong className="text-neutral-800 dark:text-neutral-200 font-bold">{rate(view.peakHour[1] / 60)}</strong> every minute</> : '—'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Legend Header */}
      <div className="mt-4 flex items-center justify-between px-1 text-xs">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 font-bold text-neutral-800 dark:text-neutral-200">
            <span className="w-2.5 h-2.5 rounded-full bg-[#E6781B] dark:bg-[#F08A2C] shadow-xs" />
            <span>Entering (darshan doors)</span>
          </div>
          {/* {view?.peakHour && (
            <div className="flex items-center gap-1.5 font-bold text-neutral-800 dark:text-neutral-200">
              <span className="w-2.5 h-2.5 rounded-sm bg-[#E6781B] dark:bg-[#F08A2C]" />
              <span>Busiest hour {hm(view.peakHour[0])}–{hm(view.peakHour[0] + 3600)} · {fmt(view.peakHour[1])} devotees</span>
            </div>
          )} */}
        </div>
      </div>

      {/* Chart container (bars) */}
      {/* taller when there is a closure, for the "Darshan Closed" boxes in the strip above the plot */}
      <div ref={barsRef} className="mt-2 w-full">
      <div className="w-full" style={{ height: `calc(${strip}px + clamp(220px, 20vw, 250px))` }}>
        {view && view.bars.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={view.chart} barCategoryGap={1} margin={{ top: 18 + strip, right: PLOT_RIGHT, left: 0, bottom: 0 }}>

              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke={light ? '#f1f5f9' : '#222735'}
              />

              <XAxis
                dataKey="x"
                type="number"
                domain={view.domain}
                ticks={view.ticks}
                tickFormatter={(t: number) => hm(t)}
                tick={axis}
                tickLine={false}
                axisLine={{ stroke: light ? '#e2e8f0' : '#272d3d' }}
                interval="preserveStartEnd"
                minTickGap={24}
                allowDataOverflow
              />

              <YAxis
                tick={axis}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                width={52}
                tickFormatter={(v: number) => fmt(v)}
              />

              <Tooltip
                cursor={{
                  stroke: light ? '#cbd5e1' : '#475569',
                  strokeWidth: 1.5,
                  strokeDasharray: '3 3',
                }}
                content={<CustomTooltip bucket={w.bucket} />}
              />

              {view.shaded.map(pz => (
                <ReferenceArea
                  key={pz.start}
                  x1={pz.x1}
                  x2={pz.x2}
                  ifOverflow="extendDomain"
                  fill={closed.hatch}
                  fillOpacity={light ? 0.22 : 0.3}
                  stroke={closed.edge}
                  strokeOpacity={0.75}
                  strokeDasharray="4 3"
                  // "Darshan Closed" and the time on a solid box in a strip above the plot, centred over its band (15 Oct
                  // 2026: a short closure in the Today view is a narrow band and its small label could hardly be read;
                  // inside the plot the box hid the line). A closure still going on is labelled left of its band's end
                  label={({ viewBox }: any) => {
                    const b = boxes.get(pz.start);
                    if (!viewBox || !b) return null;
                    const w = BOX_W, h = BOX_H, x = b.x;
                    const y = viewBox.y - 10 - h - b.row * (BOX_H + BOX_GAP);
                    return (
                      <g>
                        <rect x={x} y={y} width={w} height={h} rx={8} fill={light ? '#ffffff' : '#181b23'} stroke={closed.edge} strokeWidth={1.5} />
                        <text x={x + w / 2} y={y + 16} textAnchor="middle" fontSize={13} fontWeight={800} fill={closed.label}>
                          <tspan x={x + w / 2}>Darshan Closed</tspan>
                          <tspan x={x + w / 2} dy={16} fontSize={13.5} fontWeight={900}>{hm(pz.start)}–{pz.ongoing ? 'now' : hm(pz.end + 60)}</tspan>
                        </text>
                      </g>
                    );
                  }}
                />
              ))}

              {/* peak entry: a line and a dot on the busiest bucket */}
              {/* {view.peak && (
                <ReferenceLine x={view.peak.x} stroke={inColor} strokeDasharray="3 3" strokeOpacity={0.9}
                  // grows inward near the chart's edges ("Busiest 140 p" was cut off when the peak was the latest point)
                  label={({ viewBox }: any) => {
                    if (!viewBox) return null;
                    const f = (view.peak.x - view.domain[0]) / Math.max(1, view.domain[1] - view.domain[0]);
                    return (
                      <text x={viewBox.x} y={viewBox.y - 6} textAnchor={f > 0.88 ? 'end' : f < 0.12 ? 'start' : 'middle'}
                        fontSize={10.5} fontWeight={800} fill={light ? '#C2620F' : '#fdba74'}>
                        Busiest {rate(view.peak.inn / view.peak.minutes)} per min
                      </text>
                    );
                  }} />
              )} */}

              {/* the busiest hour's bars in the page's maroon (pale gold in dark): 15 Oct 2026, a gold strip under the
                  saffron bars could not be told apart */}
              <Bar dataKey="plot" name="In (darshan doors)" fill={inColor} radius={[2, 2, 0, 0]}
                activeBar={{ fill: light ? '#C2620F' : '#fdba74' }} isAnimationActive={false}>
                {view.chart.map(b => {
                  const ph = view.peakHour;
                  const busy = !!ph && b.x >= ph[0] && b.x < ph[0] + 3600;
                  return <Cell key={b.t} fill={busy ? (light ? PEAK_HOUR.light : PEAK_HOUR.dark) : inColor} />;
                })}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-xs text-neutral-600 dark:text-neutral-400">
            {failed ? 'Counts not available right now. Retrying…' : 'Loading data…'}
          </div>
        )}
      </div>
      </div>

      {/* Darshan Closed Badges Footer */}
      {view?.pauses.length ? (
        <div className="mt-3 pt-2.5 border-t border-neutral-100 dark:border-[#222735] flex flex-wrap items-center gap-2 text-xs">
          <div className="flex items-center gap-1.5 font-bold text-neutral-700 dark:text-neutral-300">
            <span className={`w-2 h-2 rounded-full bg-[#4D8DBA] ${view.pauses.some(pz => pz.ongoing) ? 'animate-pulse' : ''}`} />
            <span>Darshan Closed Timings:</span>
          </div>
          {view.pauses.map(pz => (
            <span
              key={pz.start}
              title="Nobody entered through the darshan doors for 10 minutes or more while people kept leaving: darshan was closed"
              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#4D8DBA]/15 dark:bg-[#4D8DBA]/20 border border-[#4D8DBA]/50 dark:border-[#4D8DBA]/40 text-[#336A91] dark:text-[#A3C8E2] shadow-2xs"
            >
              <Clock className="w-3 h-3 text-[#4D8DBA] dark:text-[#6FA6CC]" />
              <span>{hm(pz.start)}–{pz.ongoing ? 'now' : hm(pz.end + 60)}</span>
              <span className="bg-[#4D8DBA]/20 dark:bg-[#4D8DBA]/30 text-[#2B5A7B] dark:text-[#CFE2F0] px-1.5 py-0.2 rounded-sm text-[10px] font-black">
                {dur(pz.minutes)}
              </span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
    )}

    <DayLines light={light} />
    </>
  );
}
